import { yupResolver } from '@hookform/resolvers/yup';
import { Send } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import GroupsIcon from '@mui/icons-material/Groups';
import LoadingButton from '@mui/lab/LoadingButton';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import PhoneIcon from '@mui/icons-material/Phone';

import {
	Button,
	Divider,
	Grid,
	InputAdornment,
	Link,
	Stack,
	Switch,
	TextField,
	Checkbox,
	useTheme,
	FormControlLabel,
} from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StudyRequestInterface } from '../../interfaces';
import { useTranslation, studyRequestAttachmentUrl } from '../../services';
import { FileUploader } from '../FileUploader';
import Students from './Students';
import {
	studyRequestBasicValidationSchema,
	studyRequestValidationSchema,
	studyRequestValidationSchemaWithNotes,
	studyRequestAdminValidationSchema,
} from './studyRequest.validation.schema';
import styles from './StudyRequestForm.module.css';
import { PhoneMaskedInput } from '../PhoneMaskedInput';
import { useLocation } from 'react-router-dom';

interface StudyRequestFormInterface {
	onSubmit: (props: any) => void;
	values: StudyRequestInterface;
	withNotes?: boolean;
	basicFlow?: boolean;
}

const StudyRequestForm: FC<StudyRequestFormInterface> = ({
	values,
	onSubmit,
	withNotes = false,
	basicFlow = false,
}) => {
	const { t } = useTranslation();
	const location = useLocation();
	const theme = useTheme();
	const {
		palette: { mode },
	} = useTheme();
	const [loading, setLoading] = useState(false);
	let defaultIsMilitary = true;
	if (typeof values.isMilitary === 'boolean') {
		defaultIsMilitary = values.isMilitary;
	}

	const [isMilitary, setIsMilitary] = useState<boolean>(defaultIsMilitary);

	const [listOfStudents, setListOfStudents] = useState<string>('');

	const isAdminRequest = location.pathname === "/admin/study-request";

	const preSubmit = useCallback(
		async (data: StudyRequestInterface) => {
			data.isSendEmail = !isAdminRequest;
			setLoading(true);
			await onSubmit(data);
			setLoading(false);
		},
		[onSubmit, setLoading]
	);

	const handleClickToggleType = useCallback(() => {
		setIsMilitary(!isMilitary);
	}, [isMilitary, setIsMilitary]);

	const getValidationSchema = useCallback(() => {
		const propertyName = isMilitary ? 'military': 'civil';

		if (isAdminRequest) {
			return studyRequestAdminValidationSchema[propertyName];
		}

		if (withNotes) {
			return studyRequestValidationSchemaWithNotes[propertyName];
		}

		if (basicFlow) {
			return studyRequestBasicValidationSchema[propertyName];
		}

		return studyRequestValidationSchema[propertyName];
	}, [basicFlow, isAdminRequest, isMilitary, withNotes]);

	const [schema, setSchema] = useState(getValidationSchema());
	useEffect(() => {
		if (touchedFields.unit) {
			trigger('unit').then();
		}
	}, [schema]);
	

	useEffect(() => {
		const schemaValidation = getValidationSchema();
		setSchema(schemaValidation)
	}, [isMilitary, withNotes, basicFlow, getValidationSchema]);

	const {
		control,
		formState: { errors, touchedFields },
		handleSubmit,
		trigger,
		reset,
	} = useForm({
		mode: 'onTouched',
		defaultValues: values,
		resolver: yupResolver(schema),
	});

	useEffect(() => {
		if (!loading) {
			reset();
		}
	}, [loading]);

	const formReset = useCallback(() => {
		setListOfStudents('');
		reset();
	}, [reset]);

	return (
		<form noValidate autoComplete="off" onSubmit={handleSubmit(preSubmit)}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					{t('Responsible person')}
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="name"
						control={control}
						render={({ field }) => (
							<TextField
								required
								error={!!errors.name}
								disabled={isAdminRequest}
								helperText={t(errors.name?.message || '')}
								className={styles.fullWidth}
								variant="outlined"
								label={t('Name')}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="email"
						control={control}
						render={({ field }) => (
							<TextField
								required
								error={!!errors.email}
								helperText={t(errors.email?.message || '')}
								className={styles.fullWidth}
								disabled={isAdminRequest}
								variant="outlined"
								label={t('Email')}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<EmailIcon />
										</InputAdornment>
									),
								}}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<TextField
								required
								error={!!errors.phone}
								hidden={isAdminRequest}
								helperText={t(
									errors.phone?.message ||
										'Phone should be in next format +XXYYYXXXXXXX, e.g. +380991234567'
								)}
								className={styles.fullWidth}
								variant="outlined"
								label={t('Phone')}
								InputProps={{
									inputComponent: PhoneMaskedInput as any,
									endAdornment: (
										<InputAdornment position="end">
											<PhoneIcon />
										</InputAdornment>
									),
								}}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider variant="fullWidth" light={mode === 'light'} />
				</Grid>
				<Grid item xs={12} md={6}>
					{t(
						'Please use switch control to choose to use the Military Unit or Civil Organisation'
					)}
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="unit"
						control={control}
						render={({ field }) => (
							<TextField
								required
								title={t('Switch Military Unit / Civil Organisation')}
								error={!!errors.unit}
								helperText={t(errors.unit?.message || '')}
								className={styles.fullWidth}
								variant="outlined"
								InputProps={{
									startAdornment: isMilitary ? (
										<InputAdornment position="start">
											{t('M/U')}
										</InputAdornment>
									) : null,
									endAdornment: (
										<InputAdornment position="end">
											<Stack direction="row" alignItems="center">
												<GroupsIcon />
												<Switch
													defaultChecked
													value={isMilitary}
													onChange={handleClickToggleType}
												/>
												<MilitaryTechOutlinedIcon />
											</Stack>
										</InputAdornment>
									),
								}}
								label={
									isMilitary ? t('Military Unit') : t('Organisation')
								}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Divider variant="fullWidth" light={mode === 'light'} />
				</Grid>

				{!withNotes && (
					<Grid item xs={12}>
						<Students
							label={t('Students')}
							control={control}
							error={!!errors.students}
							helperText={t(errors.students?.message || '')}
							listOfStudents={listOfStudents}
							setListOfStudents={setListOfStudents}
						/>
					</Grid>
				)}
				{(!withNotes && !basicFlow && !isAdminRequest) && (
					<Grid item xs={12}>
						<Controller
							name="requestAttachments"
							control={control}
							render={({ field }) => {
								return (
									<FileUploader
										required
										label={t('Request Attachments')}
										accept=".jpg,.pdf"
										error={!!errors.requestAttachments}
										helper={
											errors.requestAttachments?.message ? (
												t(errors.requestAttachments?.message)
											) : (
												<span>
													{t(
														'Request from a military unit (description)'
													)}{' '}
													(
													<Link
														href={studyRequestAttachmentUrl}
														target="_blank"
														rel="noreferrer"
														download={t(
															'requestAttachment example filename'
														)}
														style={{
															fontStyle: 'italic',
														}}
													>
														{t('Download attachment example')}{' '}
													</Link>
													)
												</span>
											)
										}
										{...field}
									/>
								);
							}}
						/>
					</Grid>
				)}

				<Grid item xs={12}>
					<Controller
						name="description"
						control={control}
						render={({ field }) => (
							<TextField
								error={!!errors.description}
								helperText={errors.description?.message}
								className={styles.fullWidth}
								label={t('Description')}
								multiline
								rows={4}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} hidden={!withNotes}>
					<Controller
						name="notes"
						control={control}
						render={({ field }) => (
							<TextField
								required
								error={!!errors.notes}
								helperText={
									errors.notes?.message && t(errors.notes.message)
								}
								className={styles.fullWidth}
								label={t('Reason')}
								multiline
								rows={4}
								{...field}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					{
						!isAdminRequest && (
							<Stack direction="row" spacing={2}>
								<FormControlLabel
									className={styles.agreementContainer}
									style={{
										color: !!errors.agreement ? 'red': theme.palette.mode === 'dark' ? 'white': 'rgba(0, 0, 0, 0.87)'
									}}
									control={
									<Controller
										name="agreement"
										control={control}
										render={({ field: props }) => (
										<Checkbox
											{...props}
											checked={props.value}
											onChange={(e) => props.onChange(e.target.checked)}
										/>
										)}
									/>
									}
									label="Відповідно до Закону України «Про захист персональних даних», заповнюючи цю форму, ви надаєте свою  згоду на обробку персональних даних, зазначених в даній анкеті. В свою чергу, ми зобов'язуємося оберігати їх та не допустити самовільного витоку." 
								/>
							</Stack>
						)
					}

					<Stack direction="row" spacing={2}>
						<LoadingButton
							type="submit"
							variant="contained"
							loading={loading}
							loadingPosition="start"
							startIcon={<Send />}
						>
							{t('Send')}
						</LoadingButton>
						<Button variant="outlined" onClick={formReset}>
							{t(withNotes ? 'Revert to saved' : 'Clean up')}
						</Button>
					</Stack>
				</Grid>
			</Grid>
		</form>
	);
};

export default StudyRequestForm;
